import { SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { StockReportsRequest, StockReportsRestfulEvent } from './request';
import { GetReportResponse, GetReportDataResponse, SupportedTickers } from './entities';
import { StockReportsStore } from './store';
import { DurationInput } from 'luxon';

export type StockReportsManagerEvent = StockReportsRestfulEvent;

export class StockReportsManager extends Subscribable<StockReportsManagerEvent> {
  private request: StockReportsRequest;
  private store: StockReportsStore;

  constructor(session: Session) {
    super();
    this.request = new StockReportsRequest(session);
    this.store = new StockReportsStore();
  }

  public static getName = () => 'benzinga-stock-reports';

  public getReport = async (ticker: string, email: string): SafePromise<GetReportResponse> => {
    return await this.request.getReport(ticker, email);
  };

  public getReportData = async (ticker: string): SafePromise<GetReportDataResponse> => {
    return await this.request.getReportData(ticker);
  };

  public getSupportedTickers = async (
    force?: boolean,
    lastUpdatedDuration?: DurationInput,
  ): SafePromise<SupportedTickers | undefined> => {
    const stored = await this.store.getSupportedTickers(lastUpdatedDuration);
    if (force || (await this.store.shouldFetchSupportedTickers(lastUpdatedDuration))) {
      const supportedTickers = await this.request.getSupportedTickers();
      if (supportedTickers.err) {
        return { ok: stored };
      } else {
        if (supportedTickers.ok) {
          this.dispatch({
            supportedTickers: supportedTickers.ok,
            type: 'get_suppprted_tickers',
          });
          this.store.updateSupportedTickers(supportedTickers.ok);
        }
      }
      return supportedTickers;
    }
    return { ok: stored };
  };

  public isSupportedTicker = async (ticker: string, lastUpdatedDuration?: DurationInput): Promise<boolean> => {
    const supportedTickers = await this.getSupportedTickers(false, lastUpdatedDuration);
    const tickers = supportedTickers.ok?.tickers;
    if (tickers) {
      const isTickerInArray = tickers.includes(ticker);
      if (isTickerInArray) return true;
      return false;
    }
    return false;
  };
}
